/**
 * 이미지 형식 변환 유틸리티
 * JPG/JPEG/PNG가 아닌 이미지 파일을 PNG로 자동 변환합니다.
 */

/**
 * 파일이 PNG로 변환이 필요한지 확인
 * @param file 확인할 파일
 * @returns true: 변환 필요, false: 변환 불필요
 */
export const shouldConvertToPNG = (file: File): boolean => {
  const fileType = file.type.toLowerCase();
  const fileName = file.name.toLowerCase();

  // JPG, JPEG, PNG는 변환하지 않음
  if (
    fileType === 'image/jpeg' ||
    fileType === 'image/jpg' ||
    fileType === 'image/png'
  ) {
    return false;
  }

  // 파일 확장자로도 확인
  if (
    fileName.endsWith('.jpg') ||
    fileName.endsWith('.jpeg') ||
    fileName.endsWith('.png')
  ) {
    return false;
  }

  // 이미지 타입이면 변환 필요
  return fileType.startsWith('image/');
};

/**
 * 이미지 파일을 PNG 형식으로 변환
 * Canvas API를 사용하여 브라우저에서 변환 처리
 *
 * @param file 변환할 이미지 파일
 * @param quality PNG 품질 (0.0 ~ 1.0, 기본값: 0.92)
 * @returns Promise<File> 변환된 PNG 파일
 */
export const convertImageToPNG = async (
  file: File,
  quality: number = 0.92
): Promise<File> => {
  return new Promise((resolve, reject) => {
    // FileReader로 파일 읽기
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        try {
          // Canvas 생성 및 이미지 그리기
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          if (!ctx) {
            reject(new Error('Canvas context를 생성할 수 없습니다.'));
            return;
          }

          // Canvas 크기를 이미지 크기로 설정
          canvas.width = img.width;
          canvas.height = img.height;

          // 이미지를 Canvas에 그리기
          ctx.drawImage(img, 0, 0);

          // Canvas를 PNG Blob으로 변환
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('이미지를 PNG로 변환하는데 실패했습니다.'));
                return;
              }

              // 원본 파일명에서 확장자를 .png로 변경
              const originalName = file.name;
              const nameWithoutExt = originalName.substring(
                0,
                originalName.lastIndexOf('.')
              );
              const newFileName = `${nameWithoutExt}.png`;

              // Blob을 File 객체로 변환
              const convertedFile = new File([blob], newFileName, {
                type: 'image/png',
                lastModified: Date.now(),
              });

              resolve(convertedFile);
            },
            'image/png',
            quality
          );
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => {
        reject(new Error('이미지를 로드할 수 없습니다.'));
      };

      // 이미지 로드
      if (e.target?.result) {
        img.src = e.target.result as string;
      }
    };

    reader.onerror = () => {
      reject(new Error('파일을 읽을 수 없습니다.'));
    };

    // 파일을 Data URL로 읽기
    reader.readAsDataURL(file);
  });
};

/**
 * 파일 배열을 PNG로 변환 (필요한 파일만)
 * @param files 변환할 파일 배열
 * @returns Promise<File[]> 변환된 파일 배열
 */
export const convertFilesToPNG = async (files: File[]): Promise<File[]> => {
  const convertPromises = files.map(async (file) => {
    if (shouldConvertToPNG(file)) {
      try {
        return await convertImageToPNG(file);
      } catch (error) {
        console.error(`파일 변환 실패: ${file.name}`, error);
        // 변환 실패 시 원본 파일 반환
        return file;
      }
    }
    return file;
  });

  return Promise.all(convertPromises);
};
